import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { PersonalAccountCarCategoryService } from '@zaoblako/web/core/services/lk/personal-account-car-category.service';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { findDefaultCarCategoryInList } from '@zaoblako/web/shared/utils/car-category-default-from-filtered-list.util';
import type { CarCategoryFindInterface } from '@zaoblako/shared/modules/cctv/dtos/car-category/car-category-find.dto';
import type { CarCategoryQueryInterface } from '@zaoblako/shared/modules/cctv/dtos/car-category/car-category-query.dto';

import { isEqual } from 'lodash';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'crm-personal-account-car-category-single-select',
  standalone: true,
  imports: [CommonModule, SingleSelectComponent, SpinnerComponent],
  templateUrl: './personal-account-car-category-single-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PersonalAccountCarCategorySingleSelectComponent implements OnInit, OnChanges {
  @ViewChild('singleSelectComponent', { static: true }) public singleSelectComponent: SingleSelectComponent;

  @Input() public categories: CarCategoryFindInterface[] = [];
  public queryParams: CarCategoryQueryInterface = {
    limit: 40,
    offset: 0
  };

  public loading = false;
  public defaultCategoryLoading = false;
  public paginationLoading = false;
  public count = 0;

  public _selectedCategory: CarCategoryFindInterface;
  @Input() public set selectedCategory(selectedCategory: CarCategoryFindInterface) {
    if (this.customSearch) {
      this._selectedCategory = selectedCategory;
      return;
    }

    if (!selectedCategory?.id) {
      this._selectedCategory = undefined;
      return;
    }

    if (this.categories.some((category) => category.id === selectedCategory?.id)) {
      this._selectedCategory = this.categories.find((category) => category.id === selectedCategory.id);
      return;
    }

    if (selectedCategory?.id) {
      if (typeof selectedCategory.id === 'string') {
        selectedCategory.id = +selectedCategory.id;
      }

      this.personalAccountCarCategoryService.findById<CarCategoryFindInterface>(selectedCategory.id).subscribe({
        next: (_category) => {
          if (!this.categories.some((category) => category.id === _category.id)) {
            this.categories = this.categories.concat(_category);
            this.cdRef.markForCheck();
          }
          setTimeout(() => {
            this._selectedCategory = _category;
            this.cdRef.markForCheck();
          }, 30);
        }
      });
    }
  }

  @Output() public selectedCategoryChange = new EventEmitter<CarCategoryFindInterface>();

  @Input() public customSearch = false;

  @Input() public sort: string;
  @Input() public class: string;
  @Input() public styleClass: string;
  @Input() public showClear = false;
  @Input() public disabled = false;
  @Input() public isError = false;
  @Input() public isFloatLabel = false;
  @Input() public placeholder = '\u00A0';
  @Input() public appendTo: string;
  /** When bound, filters categories by room IDs; empty value clears the list */
  @Input() public roomIds: number[] | null | undefined = undefined;
  @Input() public autoApplyDefaultWhenRoomsSelected = true;

  private lastAppliedRoomIds?: number[] | null | undefined;
  private appliedDefaultForRoomFilterKey?: string;
  private pendingDefaultApplyRoomFilterKey?: string;

  @Input() public set setSelectedCategoryId(id: number) {
    if (id === null || id === undefined) {
      return;
    }

    let _id = id;
    if (typeof id === 'string') {
      _id = +id;
    }

    if (this.categories.some((category) => category.id === _id)) {
      this._selectedCategory = this.categories.find((category) => category.id === _id);
      return;
    }

    this.personalAccountCarCategoryService.findById<CarCategoryFindInterface>(_id).subscribe({
      next: (_category) => {
        if (!this.categories.some((category) => category.id === _category.id)) {
          this.categories = this.categories.concat(_category);
          this.cdRef.markForCheck();
        }
        setTimeout(() => {
          this._selectedCategory = _category;
          this.cdRef.markForCheck();
        }, 30);
      }
    });
  }

  public constructor(
    private readonly personalAccountCarCategoryService: PersonalAccountCarCategoryService,
    private readonly cdRef: ChangeDetectorRef
  ) {}

  public ngOnInit(): void {
    if (!this.shouldDeferInitialLoadToRoomFilter()) {
      this.loadCategories();
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (!changes['roomIds']) {
      return;
    }

    if (isEqual(this.lastAppliedRoomIds, this.roomIds)) {
      return;
    }

    this.lastAppliedRoomIds = this.roomIds;
    this.appliedDefaultForRoomFilterKey = undefined;
    this.defaultCategoryLoading = false;
    this.loadCategories();
  }

  private shouldDeferInitialLoadToRoomFilter(): boolean {
    return this.isRoomIdsFilterBound();
  }

  private isRoomIdsFilterBound(): boolean {
    return this.roomIds !== undefined;
  }

  private buildQueryParams(): CarCategoryQueryInterface {
    const params: CarCategoryQueryInterface = { ...this.queryParams };

    if (this.isRoomIdsFilterBound()) {
      params.roomIds = this.roomIds?.length ? this.roomIds : undefined;
    }

    return params;
  }

  private loadCategories(): void {
    if (this.customSearch) {
      return;
    }

    if (this.isRoomIdsFilterBound() && !this.roomIds?.length) {
      this.categories = [];
      this.count = 0;
      this.loading = false;
      this.paginationLoading = false;
      this.queryParams.offset = 0;
      this.cdRef.markForCheck();
      return;
    }

    this.defaultCategoryLoading = false;
    this.loading = true;
    this.queryParams.offset = 0;
    this.cdRef.markForCheck();

    this.personalAccountCarCategoryService.findWithPagination(this.buildQueryParams()).subscribe({
      next: (data) => {
        this.categories = data.items;
        this.count = data.count;
        this.loading = false;
        this.tryApplyDefaultCategoryAfterRoomsFiltered();
        this.cdRef.markForCheck();
      },
      error: () => {
        this.loading = false;
        this.cdRef.markForCheck();
      }
    });
  }

  public onSelect(category: CarCategoryFindInterface) {
    this.selectedCategoryChange.emit(category);
  }

  public onShowPanel() {
    if (!this.customSearch) {
      setTimeout(() => {
        this.singleSelectComponent?.dropdown?.scroller?.onScroll.subscribe(() => {
          const el: HTMLElement = this.singleSelectComponent?.dropdown?.scroller?.elementViewChild?.nativeElement;
          if (el.offsetHeight + Math.ceil(el.scrollTop) >= el.scrollHeight) {
            if (this.categories.length < this.count && !this.loading && !this.paginationLoading) {
              this.paginationLoading = true;
              this.queryParams.offset += this.queryParams.limit;
              this.cdRef.markForCheck();

              this.personalAccountCarCategoryService.findWithPagination(this.buildQueryParams()).subscribe({
                next: (data) => {
                  this.categories = this.categories.concat(
                    data.items.filter((item) => !this.categories.some((category) => category.id === item.id))
                  );
                  this.count = data.count;
                  this.paginationLoading = false;
                  this.cdRef.markForCheck();
                },
                error: () => {
                  this.paginationLoading = false;
                  this.cdRef.markForCheck();
                }
              });
            }
          }
        });
      }, 50);
    }
  }

  public search(name: string) {
    if (!this.customSearch) {
      this.loading = true;
      this.queryParams.search = name;
      this.queryParams.offset = 0;
      this.cdRef.markForCheck();

      this.personalAccountCarCategoryService.findWithPagination(this.buildQueryParams()).subscribe({
        next: (data) => {
          if (!data.items.some((item) => item.id === this._selectedCategory?.id) && this._selectedCategory) {
            this.categories = data.items.concat(this._selectedCategory);
          } else {
            this.categories = data.items;
          }
          this.count = data.count;
          this.loading = false;
          this.cdRef.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.cdRef.markForCheck();
        }
      });
    }
  }

  private getRoomFilterKeyForDefaultApply(): string | undefined {
    if (!this.isRoomIdsFilterBound() || !this.roomIds?.length) {
      return undefined;
    }

    return JSON.stringify({ roomIds: this.roomIds });
  }

  private tryApplyDefaultCategoryAfterRoomsFiltered(): void {
    if (!this.autoApplyDefaultWhenRoomsSelected) {
      return;
    }

    const filterKey = this.getRoomFilterKeyForDefaultApply();
    if (!filterKey || filterKey === this.appliedDefaultForRoomFilterKey) {
      return;
    }

    if (this._selectedCategory?.id) {
      this.appliedDefaultForRoomFilterKey = filterKey;
      return;
    }

    if (!this.categories.length) {
      return;
    }

    if (this.pendingDefaultApplyRoomFilterKey === filterKey) {
      return;
    }

    this.pendingDefaultApplyRoomFilterKey = filterKey;
    this.defaultCategoryLoading = true;
    this.cdRef.markForCheck();

    findDefaultCarCategoryInList(() => this.personalAccountCarCategoryService.findDefault(), this.categories)
      .pipe(
        finalize(() => {
          this.defaultCategoryLoading = false;
          this.cdRef.markForCheck();
        })
      )
      .subscribe({
        next: (match) => {
          this.pendingDefaultApplyRoomFilterKey = undefined;

          if (filterKey !== this.getRoomFilterKeyForDefaultApply()) {
            return;
          }

          this.appliedDefaultForRoomFilterKey = filterKey;

          if (this._selectedCategory?.id || !match) {
            return;
          }

          this._selectedCategory = match;
          this.selectedCategoryChange.emit(match);
          this.cdRef.markForCheck();
        }
      });
  }
}
